import React from "react";
import cn from "classnames";
import styles from "./ticket-card.module.scss";
import { SupportAgentTicket } from "@src/types/support";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import chatBotProfile from "@src/assets/svg/chat-bot-profile.svg";
import companyIcon from "@src/assets/svg/company-icon-full-gray.svg";
import sourceIcon from "@src/assets/svg/machine-icon-light-blue.svg";
import lowPriorityIcon from "@src/assets/svg/low-priority.svg";
import normalPriorityIcon from "@src/assets/svg/normal-priority.svg";
import highPriorityIcon from "@src/assets/svg/high-priority.svg";
import urgentPriorityIcon from "@src/assets/svg/urgent-priority.svg";
import InitialsAvatar from "@src/components/atoms/initials-avatar/initials-avatar";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useI18N } from "@src/language/language-context";
import { returnMachineName } from "@src/cloud-connect/support/utils/return-machine-name";
const cssClassName = "ticket-card";

interface TicketCardProps {
  ticket: SupportAgentTicket;
  selectedTicketId?: string | null;
  onSelectTicket: (ticket: SupportAgentTicket) => void;
}

// ticket ids are UUIDs and would push the card header layout; show a short
// prefix only (full id stays available via the title tooltip)
const MAX_TICKET_ID_LENGTH = 19;

const formatTicketId = (id: string) =>
  id.length <= MAX_TICKET_ID_LENGTH
    ? id
    : // drop a dangling separator so the cut never ends on a hyphen
      `${id.slice(0, MAX_TICKET_ID_LENGTH).replace(/-+$/, "")}…`;

const returnPriorityIcon = (priority: SupportAgentTicket["priority"]) => {
  switch (priority) {
    case "low":
      return lowPriorityIcon;
    case "normal":
      return normalPriorityIcon;
    case "high":
      return highPriorityIcon;
    case "urgent":
      return urgentPriorityIcon;
    default:
      return lowPriorityIcon;
  }
};

export default function TicketCard({
  ticket,
  selectedTicketId,
  onSelectTicket,
}: TicketCardProps) {
  const i18n = useI18N();

  const machineName = returnMachineName(ticket);

  const texts = {
    statusNew: i18n("modules.ticketCard.status.new"),
    statusOpen: i18n("modules.ticketCard.status.open"),
    statusPending: i18n("modules.ticketCard.status.pending"),
    statusSolved: i18n("modules.ticketCard.status.solved"),
    statusHold: i18n("modules.ticketCard.status.hold"),
    noCompanyInfo: i18n("modules.ticketCard.noCompanyInformation"),
    noMachineInfo: i18n("modules.ticketCard.noMachineInformation"),
    by: i18n("modules.ticketCard.by"),
    priorityIconAltText: i18n("modules.ticketCard.priorityIconAltText"),
    companyAltText: i18n("modules.ticketCard.companyIconAltText"),
    sourceIconAltText: i18n("modules.ticketCard.sourceIconAltText"),
    aiChatbotIconAltText: i18n("modules.ticketCard.aiChatbotIconAltText"),
    unknownUser: i18n("modules.ticketCard.unknownUser"),
  };

  const returnStatusText = (status: SupportAgentTicket["status"]) => {
    switch (status) {
      case "open":
        return texts.statusOpen;
      case "pending":
        return texts.statusPending;
      case "new":
        return texts.statusNew;
      case "solved":
        return texts.statusSolved;
      case "hold":
        return texts.statusHold;
      default:
        return "UNKNOWN";
    }
  };

  return (
    <button
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--selected`]]: ticket.id === selectedTicketId,
      })}
      onClick={() => onSelectTicket(ticket)}
    >
      <div className={styles[`${cssClassName}__left-round-border`]} />
      <div className={styles[`${cssClassName}__content`]}>
        <FlexRowContainer justifyContent="space-between">
          <FlexRowContainer>
            <img
              src={returnPriorityIcon(ticket.priority)}
              alt={texts.priorityIconAltText}
            />
            <p
              className={styles[`${cssClassName}__ticket-id`]}
              title={ticket.id}
            >
              {formatTicketId(ticket.id)}
            </p>
            {ticket.assigned_to && ticket.assigned_to.name && (
              <InitialsAvatar name={ticket.assigned_to.name} />
            )}
          </FlexRowContainer>
          <FlexRowContainer justifyContent="flex-end">
            <p
              className={cn(
                styles[`${cssClassName}__status`],
                styles[`${cssClassName}__status--${ticket.status}`],
              )}
            >
              {returnStatusText(ticket.status)}
            </p>
            <p className={styles[`${cssClassName}__last-updated`]}>
              <TimezoneDate date={ticket.last_updated_at} showRelative />
            </p>
          </FlexRowContainer>
        </FlexRowContainer>
        <h3 className={styles[`${cssClassName}__title`]}>{ticket.title}</h3>
        <FlexRowContainer>
          <div className={styles[`${cssClassName}__company-name-wrapper`]}>
            <img src={companyIcon} alt={texts.companyAltText} />
            <p className={styles[`${cssClassName}__company-name`]}>
              {ticket.org_name || texts.noCompanyInfo}
            </p>
          </div>
          <div className={styles[`${cssClassName}__machine-name-wrapper`]}>
            <img src={sourceIcon} alt={texts.sourceIconAltText} />
            <p className={styles[`${cssClassName}__machine-name`]}>
              {machineName || texts.noMachineInfo}
            </p>
          </div>
        </FlexRowContainer>
        <FlexRowContainer justifyContent="space-between">
          <p className={styles[`${cssClassName}__raised-by-name`]}>
            {`${texts.by} ${ticket.raised_by?.name || texts.unknownUser}`}
          </p>
          {ticket.handled_by_ai_chatbot && (
            <img
              src={chatBotProfile}
              alt={texts.aiChatbotIconAltText}
              className={styles[`${cssClassName}__ai-chatbot-icon`]}
            />
          )}
        </FlexRowContainer>
      </div>
    </button>
  );
}
